import { isVolatilePathSegment } from "../isVolatilePathSegment";

/**
 * Routes whose remaining segments carry no grouping value, applied after
 * identifier masking. The first match wins and the capture group becomes the
 * whole key.
 *
 * Directions encode the travel mode and an origin;destination pair in the path.
 * Neither distinguishes one failure from another — a broken route lookup is a
 * broken route lookup whether it was walking or driving — so they collapse into
 * a single key rather than one issue per mode.
 */
const ROUTE_COLLAPSE_RULES: { pattern: RegExp; replacement: string }[] = [
  { pattern: /^(\/v\d+\/directions)\/.+$/, replacement: "$1" },
];

/**
 * Reduces a request URL to a stable route key by dropping the host and query
 * string and masking identifier segments:
 * `https://api.locallogic.co/v3/schools?lat=42` becomes `/v3/schools`.
 *
 * An unparseable URL falls back to its path so the caller still gets a usable
 * key, just a noisier one.
 */
export const normalizeApiPath = (rawUrl: string): string => {
  try {
    const { pathname } = new URL(rawUrl);
    const masked = pathname
      .split("/")
      .map((segment) => (isVolatilePathSegment(segment) ? ":id" : segment))
      .join("/")
      .replace(/\/+$/, "");

    const rule = ROUTE_COLLAPSE_RULES.find(({ pattern }) =>
      pattern.test(masked),
    );

    const normalized = rule
      ? masked.replace(rule.pattern, rule.replacement)
      : masked;

    return normalized || "/";
  } catch {
    return rawUrl.split("?")[0] || "unknown";
  }
};
